/**
 * Per-platform presets for /tools/utm-builder.
 *
 * Every macro below was read from the platform's own help page on
 * 2026-10-12 (URLs in `source`). A macro that is not on that page is not in a
 * preset — if a platform revises its list, update the preset and the date.
 */

export const UTM_MACROS_VERIFIED_ON = 'October 12, 2026'

export const GA4_URL_BUILDER_HELP =
  'https://support.google.com/analytics/answer/10917952'

export type UtmPreset = {
  id: string
  label: string
  /** Values for the standard and GA4 fields; omitted keys are cleared. */
  values: Partial<Record<string, string>>
  /** Non-UTM parameters the platform's own macros are useful for. */
  extras: { key: string; value: string }[]
  /** Where the parameters go in that platform's UI. */
  pasteInto: string
  notes: string[]
  /** Every documented macro for reference, not only the ones the preset uses. */
  macros: { macro: string; meaning: string }[]
  source: { label: string; url: string }
}

export const UTM_PRESETS: UtmPreset[] = [
  {
    id: 'meta',
    label: 'Meta (Facebook & Instagram)',
    values: {
      utm_source: '{{site_source_name}}',
      utm_medium: 'paid_social',
      utm_campaign: '{{campaign.name}}',
      utm_term: '{{adset.name}}',
      utm_content: '{{ad.name}}',
      utm_id: '{{campaign.id}}',
    },
    extras: [{ key: 'placement', value: '{{placement}}' }],
    pasteInto:
      'Ads Manager → ad level → Tracking → URL parameters. Paste the “parameters only” line, not the full URL.',
    notes: [
      'Name macros freeze at the name the campaign, ad set or ad had when it was first published. Renaming later does not change the value Meta sends.',
      '{{site_source_name}} returns fb, ig, an (Audience Network), msg (Messenger) or th (Threads).',
      'Meta says it may add its own source, medium and ID parameters to ad traffic automatically.',
    ],
    macros: [
      { macro: '{{campaign.id}}', meaning: 'Campaign ID' },
      { macro: '{{adset.id}}', meaning: 'Ad set ID' },
      { macro: '{{ad.id}}', meaning: 'Ad ID' },
      { macro: '{{campaign.name}}', meaning: 'Campaign name at first publish' },
      { macro: '{{adset.name}}', meaning: 'Ad set name at first publish' },
      { macro: '{{ad.name}}', meaning: 'Ad name at first publish' },
      { macro: '{{placement}}', meaning: 'Placement, e.g. instagram_stories' },
      { macro: '{{site_source_name}}', meaning: 'fb, ig, an, msg or th' },
      { macro: '{{media_type}}', meaning: 'Advantage+ catalog ads only' },
    ],
    source: {
      label: 'Meta Business Help: dynamic URL parameters',
      url: 'https://www.facebook.com/business/help/2360940870872492',
    },
  },
  {
    id: 'google',
    label: 'Google Ads',
    values: {
      utm_source: 'google',
      utm_medium: 'cpc',
      utm_campaign: 'campaign_name',
      utm_term: '{keyword}',
      utm_content: '{creative}',
      utm_id: '{campaignid}',
    },
    extras: [
      { key: 'adgroupid', value: '{adgroupid}' },
      { key: 'matchtype', value: '{matchtype}' },
      { key: 'device', value: '{device}' },
      { key: 'network', value: '{network}' },
    ],
    pasteInto:
      'Campaign or account settings → Campaign URL options → Final URL suffix. Paste the “parameters only” line.',
    notes: [
      'ValueTrack has no campaign-name macro, so type the campaign name into utm_campaign yourself and let {campaignid} carry the ID.',
      'Auto-tagging adds gclid to every click and is on by default for new accounts. Google states that GA4 prioritises auto-tagging over manual UTMs, so with a linked GA4 property these tags mainly serve other analytics tools.',
      '{keyword} is blank when an ad matches without a keyword (AI Max for Search, DSA, Performance Max).',
    ],
    macros: [
      { macro: '{campaignid}', meaning: 'Campaign ID' },
      { macro: '{adgroupid}', meaning: 'Ad group ID' },
      { macro: '{creative}', meaning: 'Ad ID' },
      { macro: '{keyword}', meaning: 'Matched keyword' },
      { macro: '{matchtype}', meaning: 'e, p, b, or a (AI Max keywordless)' },
      { macro: '{device}', meaning: 'm, t or c' },
      { macro: '{network}', meaning: 'g, s, d, ytv, vp, gtv, x or e' },
      { macro: '{targetid}', meaning: 'Keyword, audience or DSA target ID' },
      { macro: '{placement}', meaning: 'Content site (Display)' },
      { macro: '{loc_physical_ms}', meaning: 'Location ID of the click' },
      { macro: '{assetgroupid}', meaning: 'Asset group ID (Performance Max)' },
    ],
    source: {
      label: 'Google Ads Help: ValueTrack parameters',
      url: 'https://support.google.com/google-ads/answer/6305348',
    },
  },
  {
    id: 'tiktok',
    label: 'TikTok Ads',
    values: {
      utm_source: 'tiktok',
      utm_medium: 'paid_social',
      utm_campaign: '__CAMPAIGN_NAME__',
      utm_term: '__AID_NAME__',
      utm_content: '__CID_NAME__',
      utm_id: '__CAMPAIGN_ID__',
    },
    extras: [{ key: 'placement', value: '__PLACEMENT__' }],
    pasteInto:
      'Ad level → Destination → URL parameters (or the website URL itself). TikTok can also add utm_source and utm_medium for you.',
    notes: [
      'In TikTok, __AID__ is the ad group and __CID__ is the creative — not the other way round.',
      'Upgraded Smart+ campaigns use __ADID_V2_NAME__ and __ADID_V2__ for the ad; swap them in for utm_content if that is what you run.',
    ],
    macros: [
      { macro: '__CAMPAIGN_NAME__', meaning: 'Campaign name' },
      { macro: '__CAMPAIGN_ID__', meaning: 'Campaign ID' },
      { macro: '__AID_NAME__', meaning: 'Ad group name' },
      { macro: '__AID__', meaning: 'Ad group ID' },
      { macro: '__CID_NAME__', meaning: 'Creative name' },
      { macro: '__CID__', meaning: 'Creative ID' },
      { macro: '__ADID_V2_NAME__', meaning: 'Ad name (upgraded Smart+)' },
      { macro: '__ADID_V2__', meaning: 'Ad ID (upgraded Smart+)' },
      { macro: '__PLACEMENT__', meaning: 'Placement type (TikTok, Pangle)' },
    ],
    source: {
      label: 'TikTok Ads Manager Help: About UTM parameters',
      url: 'https://ads.tiktok.com/resources/help/article/track-offsite-web-events-with-utm-parameters',
    },
  },
  {
    id: 'linkedin',
    label: 'LinkedIn Ads',
    values: {
      utm_source: 'linkedin',
      utm_medium: 'paid_social',
      utm_campaign: '{{CAMPAIGN_NAME}}',
      utm_term: '{{AD_SET_NAME}}',
      utm_content: '{{AD_NAME}}',
      utm_id: '{{CAMPAIGN_ID}}',
    },
    extras: [],
    pasteInto:
      'Campaign Manager → account or campaign level → URL parameters. Dynamic parameters can only be set there, not per ad.',
    notes: [
      'These are the post-October-2025 names (campaign → ad set → ad). Older ad sets update to them automatically; new or edited ones must use them.',
      'LinkedIn accepts only letters, digits, _ + and - in static values, so keep spaces set to underscores or hyphens.',
      'Campaign-level parameters are added to, not swapped for, any parameters already in the ad’s destination URL.',
    ],
    macros: [
      { macro: '{{ACCOUNT_ID}}', meaning: 'Ad account ID' },
      { macro: '{{ACCOUNT_NAME}}', meaning: 'Ad account name' },
      { macro: '{{CAMPAIGN_ID}}', meaning: 'Campaign ID' },
      { macro: '{{CAMPAIGN_NAME}}', meaning: 'Campaign name' },
      { macro: '{{AD_SET_ID}}', meaning: 'Ad set ID' },
      { macro: '{{AD_SET_NAME}}', meaning: 'Ad set name' },
      { macro: '{{AD_ID}}', meaning: 'Ad ID' },
      { macro: '{{AD_NAME}}', meaning: 'Ad name' },
    ],
    source: {
      label: 'LinkedIn Help: URL tracking parameters in Campaign Manager',
      url: 'https://www.linkedin.com/help/lms/answer/a5968064',
    },
  },
  {
    id: 'microsoft',
    label: 'Microsoft Advertising',
    values: {
      utm_source: 'bing',
      utm_medium: 'cpc',
      utm_campaign: '{Campaign}',
      utm_term: '{keyword:default}',
      utm_content: '{AdId}',
      utm_id: '{CampaignId}',
    },
    extras: [
      { key: 'adgroup', value: '{AdGroup}' },
      { key: 'matchtype', value: '{MatchType}' },
      { key: 'device', value: '{Device}' },
      { key: 'network', value: '{Network}' },
    ],
    pasteInto:
      'Account, campaign or ad group → URL options → Final URL suffix. Paste the “parameters only” line.',
    notes: [
      'Unlike Google, Microsoft has name macros: {Campaign} and {AdGroup}.',
      'In {keyword:default}, the text after the colon is what Microsoft inserts if the keyword would make the URL too long. Keep it free of spaces.',
      'Microsoft can auto-tag clicks with msclkid, its own click ID.',
    ],
    macros: [
      { macro: '{Campaign}', meaning: 'Campaign name' },
      { macro: '{CampaignId}', meaning: 'Campaign ID' },
      { macro: '{AdGroup}', meaning: 'Ad group (or asset group) name' },
      { macro: '{AdGroupId}', meaning: 'Ad group ID' },
      { macro: '{AdId}', meaning: 'Ad ID' },
      { macro: '{keyword:default}', meaning: 'Matched keyword' },
      { macro: '{MatchType}', meaning: 'e, p or b' },
      { macro: '{BidMatchType}', meaning: 'be, bp or bb' },
      { macro: '{Device}', meaning: 'm, t or c' },
      { macro: '{Network}', meaning: 'o, s or a' },
      { macro: '{TargetId}', meaning: 'Keyword, audience or target ID' },
      { macro: '{msclkid}', meaning: 'Click ID' },
    ],
    source: {
      label: 'Microsoft Advertising Help: tracking and URL parameters',
      url: 'https://help.ads.microsoft.com/apex/index/3/en/56799',
    },
  },
  {
    id: 'chatgpt',
    label: 'ChatGPT Ads',
    values: {
      utm_source: 'chatgpt',
      utm_medium: 'cpc',
      utm_campaign: '{campaign_id}',
      utm_term: '{ad_group_id}',
      utm_content: '{ad_id}',
    },
    extras: [{ key: 'oai_account', value: '{ad_account_id}' }],
    pasteInto:
      'Ads Manager → ⋯ → Edit campaign (or ad group, or ad) → Landing page query parameters.',
    notes: [
      'OpenAI documents four macros, all IDs. There is no name macro, so keep an ID-to-name lookup for reports.',
      'The most specific level wins: ad URL, then ad, then ad group, then campaign. A key already in an ad’s destination URL is never overwritten.',
    ],
    macros: [
      { macro: '{campaign_id}', meaning: 'Campaign ID' },
      { macro: '{ad_group_id}', meaning: 'Ad group ID' },
      { macro: '{ad_id}', meaning: 'Ad ID' },
      { macro: '{ad_account_id}', meaning: 'Ad account ID' },
    ],
    source: {
      label: 'OpenAI Help: Measure Results (ChatGPT Ads)',
      url: 'https://help.openai.com/en/articles/20001214-measure-results',
    },
  },
]
